import React from "react";
import { motion } from "framer-motion";
import { Target, Repeat, CalendarRange } from "lucide-react";
import { STRATEGY_GOALS, MONTHLY_MIX, MONTHLY_PLAN, ANNUAL_CALENDAR } from "../data/content";

const Box = ({ icon: Icon, kicker, title, intro, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay }}
    className="rounded-[2rem] bg-white border border-gray-100 shadow-sm p-8 sm:p-10"
  >
    <div className="flex flex-col sm:flex-row sm:items-start gap-5">
      <span className="inline-flex h-16 w-16 flex-none items-center justify-center rounded-2xl bg-brand text-white shadow-lg shadow-brand/25">
        <Icon size={30} strokeWidth={2.5} />
      </span>
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{kicker}</span>
        <h3 className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-brand-deep">{title}</h3>
        <p className="mt-3 font-body text-base sm:text-lg text-ink leading-relaxed max-w-3xl">{intro}</p>
      </div>
    </div>
    <div className="mt-8">{children}</div>
  </motion.div>
);

export const Strategy = () => {
  return (
    <section id="strategie" className="py-20 sm:py-28" data-testid="strategy-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand">Strategie</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-brand-deep">
            Cosa comunicare ogni mese
          </h1>
          <p className="mt-4 font-body text-base sm:text-lg text-ink leading-relaxed">
            Un piano semplice e ripetibile per restare presente senza stancare i clienti.
            Parti dagli obiettivi, segui il mix mensile e lasciati ispirare dal calendario.
          </p>
        </div>

        <div className="mt-14 space-y-6">
          {/* Box 1 — Obiettivi */}
          <Box
            icon={Target}
            kicker="Il punto di partenza"
            title="I tuoi 5 obiettivi"
            intro="Prima di scrivere qualsiasi messaggio, chiediti perché lo stai inviando. Ogni comunicazione efficace punta a uno di questi cinque obiettivi: averli chiari ti aiuta a scegliere il tono giusto e a non inviare notifiche a vuoto."
            delay={0}
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="strategy-goals">
              {STRATEGY_GOALS.map((g, i) => (
                <div key={i} className="flex items-start gap-4 rounded-2xl bg-[#F9FAFB] border border-gray-100 p-5">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand text-white font-heading font-extrabold">{i + 1}</span>
                  <span className="font-body text-ink text-[16px] leading-snug pt-1.5">{g}</span>
                </div>
              ))}
            </div>
          </Box>

          {/* Box 2 — Mix mensile */}
          <Box
            icon={Repeat}
            kicker="Il ritmo giusto"
            title="Il mix mensile"
            intro="Un buon mese alterna contenuti utili e offerte, senza mai stancare chi ti segue. Questo è il mix consigliato di comunicazioni e come distribuirlo lungo il mese, dall'inizio alla fine."
            delay={0.05}
          >
            <div className="grid lg:grid-cols-2 gap-6" data-testid="strategy-mix">
              <div>
                <p className="font-heading text-sm font-bold uppercase tracking-wider text-brand mb-4">Il mix consigliato</p>
                <div className="space-y-3">
                  {MONTHLY_MIX.map((m, i) => (
                    <div key={i} className="flex items-center gap-4 rounded-2xl bg-brand-light/20 p-4">
                      <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-white font-heading text-xl font-extrabold text-brand shadow-sm">{m.count}</span>
                      <div>
                        <p className="font-body font-semibold text-brand-deep text-[16px]">{m.label}</p>
                        <p className="font-body text-sm text-ink">{m.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-heading text-sm font-bold uppercase tracking-wider text-brand mb-4">Come distribuirlo nel mese</p>
                <div className="space-y-3">
                  {MONTHLY_PLAN.map((p, i) => (
                    <div key={i} className="rounded-2xl bg-[#F9FAFB] border border-gray-100 p-4">
                      <p className="font-heading font-bold text-brand-deep">{p.period}</p>
                      <p className="font-body text-ink text-[15px] mt-1">{p.action}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Box>

          {/* Box 3 — Calendario annuale */}
          <Box
            icon={CalendarRange}
            kicker="Sempre uno spunto pronto"
            title="Il calendario annuale"
            intro="Non sai cosa comunicare? Parti dalle ricorrenze. Ecco le occasioni principali mese per mese su cui costruire notifiche, promozioni ed eventi: usale come punto di partenza e adattale alla tua attività."
            delay={0.1}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="strategy-calendar">
              {ANNUAL_CALENDAR.map((c) => (
                <div key={c.month} className="rounded-2xl border border-gray-100 bg-[#F9FAFB] p-4 transition-colors hover:border-brand/30">
                  <p className="font-heading text-base font-bold text-brand">{c.month}</p>
                  <ul className="mt-2 space-y-1">
                    {c.items.map((it) => (
                      <li key={it} className="font-body text-[13px] text-ink leading-snug">• {it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Box>
        </div>
      </div>
    </section>
  );
};
